import { useEffect, useState } from 'react'
import { Alert, Button, Space, Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import { axiosInstance } from '../utils/axios-interceptor'

export const isDriveFile = value => {
	try {
		const url = new URL(value)
		if (url.protocol !== 'https:' || !['drive.google.com', 'docs.google.com'].includes(url.hostname)) return false
		if (url.username || url.password || (url.port && url.port !== '443')) return false
		return (
			/^\/(file|document|spreadsheets|presentation|drawings)\/d\/[\w-]+(?:\/.*)?$/.test(url.pathname) ||
			(url.hostname === 'drive.google.com' &&
				['/open', '/uc'].includes(url.pathname) &&
				url.searchParams.getAll('id').length === 1 &&
				/^[\w-]+$/.test(url.searchParams.get('id') || ''))
		)
	} catch {
		return false
	}
}

export default function SdrDocumentViewer({ apiUrl, dealId, url, title }) {
	const { t } = useTranslation('deal')
	const [state, setState] = useState({})
	const [attempt, setAttempt] = useState(0)
	useEffect(() => {
		const controller = new AbortController()
		let objectUrl
		setState({})
		axiosInstance
			.get(`${apiUrl}/deals/${dealId}/materials/content`, {
				params: { url },
				responseType: 'blob',
				signal: controller.signal
			})
			.then(response => {
				if (controller.signal.aborted) return
				const type = response.data.type.split(';')[0]
				const preview = [
					'application/pdf',
					'image/png',
					'image/jpeg',
					'image/gif',
					'image/webp',
					'text/plain'
				].includes(type)
				// Never render active content returned by an unexpected server response.
				const blob = preview ? response.data : new Blob([response.data], { type: 'application/octet-stream' })
				objectUrl = URL.createObjectURL(blob)
				const disposition = response.headers?.['content-disposition'] || ''
				const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
				let filename
				try {
					filename = encodedName && decodeURIComponent(encodedName)
				} catch {
					/* use the display title */
				}
				setState({ objectUrl, preview, type, filename })
			})
			.catch(error => {
				if (!controller.signal.aborted) setState({ error: error.response?.status || 'network' })
			})
		return () => {
			controller.abort()
			if (objectUrl) URL.revokeObjectURL(objectUrl)
		}
	}, [apiUrl, dealId, url, attempt])

	if (state.error)
		return (
			<Alert
				type='warning'
				showIcon
				message={t(state.error === 503 ? 'document_unconfigured' : 'document_unavailable')}
				action={<Button onClick={() => setAttempt(value => value + 1)}>{t('document_retry')}</Button>}
			/>
		)
	if (!state.objectUrl) return <Spin aria-label={t('document_loading')} />
	return (
		<Space direction='vertical' style={{ width: '100%' }}>
			<Button
				href={state.objectUrl}
				download={state.filename || `${title || 'document'}${state.type === 'application/pdf' ? '.pdf' : ''}`}
			>
				{t('document_download')}
			</Button>
			{state.preview ? (
				<iframe
					title={title || t('materials')}
					src={state.objectUrl}
					style={{ border: 0, borderRadius: 10, width: '100%', height: '65vh', minHeight: 320 }}
				/>
			) : (
				<Alert type='info' message={t('document_download_only')} />
			)}
		</Space>
	)
}
